import { CardElement } from '@stripe/react-stripe-js';
import { type StripeCardElementOptions } from '@stripe/stripe-js';
import { useTheme } from 'next-themes';
import { memo, useEffect, useMemo, useState } from 'react';

import { red, redDark, v3 } from '@air/colors';

import { CreditCardsIcons } from '~/components/Workspace/Checkout/PaymentFields/CreditCardsIcons';

export type PaymentFieldsProps = {
  disabled?: boolean;
};

export const PaymentFields = memo(({ disabled }: PaymentFieldsProps) => {
  const theme = useTheme();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isDark = theme.theme === 'dark';

  const fieldStyles = useMemo(
    () => ({
      base: {
        color: isDark ? v3.gray[12].dark : v3.gray[12].light,
        '::placeholder': { color: isDark ? v3.gray[8].dark : v3.gray[8].light },
      },
      invalid: {
        color: isDark ? redDark.red10.hex : red.red10.hex,
      },
    }),
    [isDark],
  );

  const cardElementOptions = useMemo<StripeCardElementOptions>(
    () => ({ style: fieldStyles, disabled }),
    [fieldStyles, disabled],
  );

  if (!isMounted) {
    return null;
  }

  return (
    <div className="flex w-full flex-col font-medium ads:text-metadata text-14 text-gray-10">
      <div className="mb-2 flex justify-between">
        <div className="ads:text-secondary text-12 text-gray-11">Credit card or debit card</div>
        <CreditCardsIcons className="mb-1.5" />
      </div>
      <CardElement options={cardElementOptions} />
      <div className="mt-2.5 hidden whitespace-nowrap font-normal text-14 text-red-9">This value is invalid.</div>
    </div>
  );
});

PaymentFields.displayName = 'PaymentFields';
